import { parseUnitSuffix } from "./display/parseUnits.js";

/**
 * 방치형 큰 수치 값 객체 — 가수(mantissa) + 1000-base 지수(exponent).
 * JS number 정밀도(2^53)를 넘는 누적량(골드/데미지/HP/비용)을 안전하게 표현·연산한다.
 *
 * 내부 규약(idleblade unity-bignumber 차용):
 * - mantissa: 부호 포함. 0이 아니면 |mantissa| ∈ [1, 1000). 0이면 (0, 0).
 * - exponent: 1000-base 정수. 실제 값 = mantissa × 1000^exponent.
 *
 * 불변(immutable) — 모든 연산은 새 인스턴스를 반환한다.
 * 직렬화는 toString()의 invariant e표기(`-1.234560e5`)로 locale 무관 round-trip 안전.
 */
export class BigNumber {
  readonly mantissa: number;
  readonly exponent: number;

  private constructor(mantissa: number, exponent: number) {
    this.mantissa = mantissa;
    this.exponent = exponent;
  }

  static readonly ZERO = new BigNumber(0, 0);
  static readonly ONE = new BigNumber(1, 0);

  /** 정규화 후 인스턴스 생성 — mantissa를 [1,1000)로 맞추고 exponent 보정. */
  static fromMantissaExp(mantissa: number, exponent: number): BigNumber {
    if (!Number.isFinite(mantissa) || mantissa === 0) return BigNumber.ZERO;
    const sign = mantissa < 0 ? -1 : 1;
    let a = Math.abs(mantissa);
    let e = exponent;
    // 1000-base 자릿 그룹으로 1차 시프트.
    const shift = Math.floor(Math.log10(a) / 3);
    if (shift !== 0) {
      a /= Math.pow(1000, shift);
      e += shift;
    }
    // 부동소수 오차 보정.
    while (a >= 1000) {
      a /= 1000;
      e += 1;
    }
    while (a < 1) {
      a *= 1000;
      e -= 1;
    }
    return new BigNumber(sign * a, e);
  }

  /** JS number → BigNumber. */
  static fromNumber(n: number): BigNumber {
    if (!Number.isFinite(n) || n === 0) return BigNumber.ZERO;
    return BigNumber.fromMantissaExp(n, 0);
  }

  /**
   * 문자열 → BigNumber. 우선순위:
   * ① e표기(`-1.5e3`) — 직렬화 SSoT, 최우선(`1e3`의 e가 단위로 오인되는 것 차단).
   * ② 단위 접미사(`1.5B`/`2.3aa`) — 치트/수동 입력 흡수(ShortScale 대문자 → 알파벳).
   * ③ 일반 숫자 문자열.
   * 잘못된 입력은 ZERO (호출부가 fallback 처리).
   */
  static fromString(s: string): BigNumber {
    const trimmed = s.trim();
    if (trimmed === "") return BigNumber.ZERO;
    // ① e표기 (직렬화 SSoT — 최우선).
    const e = /^(-?\d*\.?\d+)e(-?\d+)$/i.exec(trimmed);
    if (e) {
      const mant = Number(e[1]);
      const exp = Number(e[2]);
      if (Number.isFinite(mant) && Number.isFinite(exp)) {
        return BigNumber.fromMantissaExp(mant, exp);
      }
      return BigNumber.ZERO;
    }
    // ② 단위 접미사 (치트/수동 입력).
    const unit = parseUnitSuffix(trimmed);
    if (unit) return BigNumber.fromMantissaExp(unit.mantissa, unit.exponent);
    // ③ 일반 숫자.
    const plain = Number(trimmed);
    return Number.isFinite(plain) ? BigNumber.fromNumber(plain) : BigNumber.ZERO;
  }

  isZero(): boolean {
    return this.mantissa === 0;
  }

  isNegative(): boolean {
    return this.mantissa < 0;
  }

  /**
   * 값이 NaN인지. 정규화된 정상값은 음수 exponent(소수)라도 false —
   * 레퍼런스의 `exponent < 0` NaN 조건은 phaser가 음수 exponent를 정상 소수로 쓰므로 채용하지 않는다.
   */
  isNaN(): boolean {
    return Number.isNaN(this.mantissa);
  }

  /** 값이 JS double 표현 범위를 초과(±Infinity)하는지. */
  isInfinity(): boolean {
    return !Number.isFinite(this.toNumber());
  }

  abs(): BigNumber {
    return this.mantissa < 0 ? new BigNumber(-this.mantissa, this.exponent) : this;
  }

  negate(): BigNumber {
    return this.isZero() ? this : new BigNumber(-this.mantissa, this.exponent);
  }

  /** mantissa를 정수로 반올림 (exponent 유지). 레퍼런스 Round 정합 — 1.5e3 → 2e3. */
  round(): BigNumber {
    if (this.isZero()) return this;
    return BigNumber.fromMantissaExp(Math.round(this.mantissa), this.exponent);
  }

  /** log10(|value|) = log10(|mantissa|) + 3·exponent. 동양 표기 rebasing의 핵심. ZERO는 -Infinity. */
  log10(): number {
    if (this.isZero()) return -Infinity;
    return Math.log10(Math.abs(this.mantissa)) + 3 * this.exponent;
  }

  /** 표현 가능 범위면 JS number, 초과 시 ±Infinity. */
  toNumber(): number {
    return this.mantissa * Math.pow(1000, this.exponent);
  }

  /** invariant 직렬화 (e표기). round-trip 안전 — 저장/복원 전용. */
  toString(): string {
    if (this.isZero()) return "0e0";
    return `${this.mantissa.toFixed(6)}e${this.exponent}`;
  }

  add(o: BigNumber): BigNumber {
    if (this.isZero()) return o;
    if (o.isZero()) return this;
    // 지수가 큰 쪽 기준으로 정렬 후 가산.
    const [hi, lo] = this.exponent >= o.exponent ? [this, o] : [o, this];
    const diff = hi.exponent - lo.exponent;
    // 10^21 이상 차이는 double 정밀도 밖 → 작은 쪽 무시.
    if (diff > 7) return hi;
    const loM = lo.mantissa / Math.pow(1000, diff);
    return BigNumber.fromMantissaExp(hi.mantissa + loM, hi.exponent);
  }

  sub(o: BigNumber): BigNumber {
    return this.add(o.negate());
  }

  mul(o: BigNumber): BigNumber {
    if (this.isZero() || o.isZero()) return BigNumber.ZERO;
    return BigNumber.fromMantissaExp(this.mantissa * o.mantissa, this.exponent + o.exponent);
  }

  div(o: BigNumber): BigNumber {
    if (o.isZero()) throw new Error("BigNumber: division by zero");
    if (this.isZero()) return BigNumber.ZERO;
    return BigNumber.fromMantissaExp(this.mantissa / o.mantissa, this.exponent - o.exponent);
  }

  /** 실수 지수 거듭제곱 (log10 경유). 음수 base는 절댓값으로 처리. */
  pow(exp: number): BigNumber {
    if (exp === 0) return BigNumber.ONE;
    if (this.isZero()) return BigNumber.ZERO;
    const l = this.log10() * exp;
    const e = Math.floor(l / 3);
    const mant = Math.pow(10, l - 3 * e);
    return BigNumber.fromMantissaExp(mant, e);
  }

  /** -1 | 0 | 1 (this vs o). */
  compare(o: BigNumber): -1 | 0 | 1 {
    const sa = Math.sign(this.mantissa);
    const sb = Math.sign(o.mantissa);
    if (sa !== sb) return sa < sb ? -1 : 1;
    if (sa === 0) return 0; // 둘 다 0
    // 같은 부호 (둘 다 nonzero).
    if (this.exponent !== o.exponent) {
      const cmp = this.exponent < o.exponent ? -1 : 1;
      return (sa > 0 ? cmp : -cmp) as -1 | 1;
    }
    if (this.mantissa === o.mantissa) return 0;
    return this.mantissa < o.mantissa ? -1 : 1;
  }

  eq(o: BigNumber): boolean {
    return this.compare(o) === 0;
  }
  gt(o: BigNumber): boolean {
    return this.compare(o) > 0;
  }
  gte(o: BigNumber): boolean {
    return this.compare(o) >= 0;
  }
  lt(o: BigNumber): boolean {
    return this.compare(o) < 0;
  }
  lte(o: BigNumber): boolean {
    return this.compare(o) <= 0;
  }

  min(o: BigNumber): BigNumber {
    return this.lte(o) ? this : o;
  }
  max(o: BigNumber): BigNumber {
    return this.gte(o) ? this : o;
  }
}

/** number | string | BigNumber → BigNumber (세이브 복원·치트 입력 흡수용). */
export function toBigNumber(v: number | string | BigNumber): BigNumber {
  if (v instanceof BigNumber) return v;
  if (typeof v === "number") return BigNumber.fromNumber(v);
  return BigNumber.fromString(v);
}
